import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Upload, FileText, Pencil, Ban } from "lucide-react";
import { API, authHeaders, money, inputCls, labelCls, Modal, VoidDialog, uploadFinanceFile } from "./shared";
import ReceiptScanner from "./ReceiptScanner";

export default function ExpensesModule({ token, onAuthError, options, vehicles }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [voiding, setVoiding] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    axios.get(`${API}/expenses`, authHeaders(token)).then((r) => setItems(r.data || []))
      .catch((e) => { if (e.response?.status === 401) onAuthError?.(); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const doVoid = async (reason) => {
    setBusy(true);
    try {
      await axios.post(`${API}/expenses/${voiding.id}/void`, { reason }, authHeaders(token));
      toast.success("Expense voided");
      setVoiding(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not void"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#F2B705]" /></div>;
  const active = items.filter((i) => i.status !== "voided");
  const total = active.reduce((s, i) => s + Number(i.amount || 0), 0);
  const vname = (id) => { const v = vehicles.find((x) => x.id === id); return v ? `${v.year} ${v.make} ${v.model}` : ""; };

  return (
    <div className="space-y-4" data-testid="expenses-module">
      <ReceiptScanner token={token} options={options} vehicles={vehicles} onFiled={load} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-white font-bold">Manual Expense Ledger</h3>
          <p className="text-white/45 text-sm">{active.length} expenses · {money(total)} total business spend.</p>
        </div>
        <button data-testid="add-expense-btn" onClick={() => setEditing("new")}
          className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#F2B705] text-black text-sm font-bold hover:bg-white transition-colors">
          <Plus size={15} /> Add Expense
        </button>
      </div>

      <div className="bg-[#0C0C0C] border border-white/10 rounded-xl overflow-hidden">
        {items.length === 0 && <p className="text-white/35 text-sm p-5">No expenses logged yet.</p>}
        <div className="divide-y divide-white/5">
          {items.map((e) => (
            <div key={e.id} data-testid={`expense-row-${e.id}`} className={`px-4 py-3 ${e.status === "voided" ? "opacity-45" : ""}`}>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-white/85 font-semibold">{e.category}</span>
                <span className="text-white/45">· {e.vendor || "no vendor"}</span>
                {e.status === "voided" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 font-bold">VOIDED</span>}
                <span className="ml-auto font-bold text-red-400">−{money(e.amount)}</span>
              </div>
              <div className="text-white/45 text-xs mt-1">
                {e.date}{e.payment_method ? ` · ${e.payment_method}` : ""}{e.vehicle_id ? ` · ${vname(e.vehicle_id)}` : ""}
                {e.entered_by ? ` · entered by ${e.entered_by}` : ""}
                {e.last_modified_by ? ` · edited by ${e.last_modified_by}` : ""}
                {e.notes ? ` · ${e.notes}` : ""}
              </div>
              {e.status === "voided" && <div className="text-red-400/80 text-xs mt-0.5">Voided by {e.voided_by} — {e.void_reason}</div>}
              <div className="flex items-center gap-3 mt-1.5">
                {e.receipt_path && (
                  <a href={`${API}/files/${e.receipt_path}?auth=${token}`} target="_blank" rel="noreferrer"
                    className="text-[#F2B705] hover:underline flex items-center gap-1 text-xs"><FileText size={12} /> Receipt</a>
                )}
                {e.status !== "voided" && <>
                  <button data-testid={`exp-edit-${e.id}`} onClick={() => setEditing(e)} className="flex items-center gap-1 text-xs text-white/50 hover:text-white"><Pencil size={12} /> Edit</button>
                  <button data-testid={`exp-void-${e.id}`} onClick={() => setVoiding(e)} className="flex items-center gap-1 text-xs text-white/50 hover:text-red-400"><Ban size={12} /> Void</button>
                </>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <ExpenseForm token={token} options={options} vehicles={vehicles}
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
      {voiding && <VoidDialog label="Expense" busy={busy} onConfirm={doVoid} onClose={() => setVoiding(null)} />}
    </div>
  );
}

function ExpenseForm({ token, options, vehicles, initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial ? {
    date: initial.date || "", category: initial.category || "Maintenance", amount: String(initial.amount ?? ""),
    vendor: initial.vendor || "", vehicle_id: initial.vehicle_id || "", payment_method: initial.payment_method || "", notes: initial.notes || "",
    booking_id: initial.booking_id || "", period_label: initial.period_label || "",
  } : { date: new Date().toISOString().slice(0, 10), category: "Maintenance", amount: "", vendor: "", vehicle_id: "", payment_method: "Cash", notes: "", booking_id: "", period_label: "" });
  const [receipt, setReceipt] = useState(initial?.receipt_path ? { path: initial.receipt_path, content_type: initial.receipt_content_type, filename: initial.receipt_filename } : null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { setReceipt(await uploadFinanceFile(token, file)); toast.success("Receipt attached"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Enter an amount greater than $0"); return; }
    setSaving(true);
    const payload = { ...form, amount: Number(form.amount),
      receipt_path: receipt?.path || null, receipt_content_type: receipt?.content_type || null, receipt_filename: receipt?.filename || null };
    try {
      if (initial) {
        await axios.patch(`${API}/expenses/${initial.id}`, payload, authHeaders(token));
        toast.success("Expense updated");
      } else {
        await axios.post(`${API}/expenses`, payload, authHeaders(token));
        toast.success("Expense logged");
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={initial ? "Edit Expense" : "Add Expense"} onClose={onClose} wide>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div><label className={labelCls}>Date</label>
          <input data-testid="exp-form-date" type="date" className={inputCls} value={form.date} onChange={(e) => set("date", e.target.value)} /></div>
        <div><label className={labelCls}>Category</label>
          <select data-testid="exp-form-category" className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)}>
            {[...new Set([...(options?.expense_categories || []), form.category])].map((c) => <option key={c} value={c}>{c}</option>)}
          </select></div>
        <div><label className={labelCls}>Amount *</label>
          <input data-testid="exp-form-amount" type="number" step="0.01" className={inputCls} value={form.amount} onChange={(e) => set("amount", e.target.value)} /></div>
        <div><label className={labelCls}>Vendor</label>
          <input data-testid="exp-form-vendor" className={inputCls} value={form.vendor} onChange={(e) => set("vendor", e.target.value)} /></div>
        <div><label className={labelCls}>Vehicle (optional)</label>
          <select data-testid="exp-form-vehicle" className={inputCls} value={form.vehicle_id} onChange={(e) => set("vehicle_id", e.target.value)}>
            <option value="">No vehicle</option>
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.year} {v.make} {v.model}</option>)}
          </select></div>
        <div><label className={labelCls}>Payment Method</label>
          <select data-testid="exp-form-method" className={inputCls} value={form.payment_method} onChange={(e) => set("payment_method", e.target.value)}>
            <option value="">—</option>
            {(options?.payment_methods || []).map((m) => <option key={m} value={m}>{m}</option>)}
          </select></div>
        <div className="col-span-2 md:col-span-3"><label className={labelCls}>Notes</label>
          <input data-testid="exp-form-notes" className={inputCls} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
        {form.category === "All-In Coverage — Paid to Provider" && (
          <>
            <div><label className={labelCls}>Booking ID (optional link)</label>
              <input data-testid="exp-form-booking-id" className={inputCls} placeholder="Link to a specific booking" value={form.booking_id} onChange={(e) => set("booking_id", e.target.value)} /></div>
            <div><label className={labelCls}>Period (optional, e.g. "October 2026")</label>
              <input data-testid="exp-form-period" className={inputCls} value={form.period_label} onChange={(e) => set("period_label", e.target.value)} /></div>
            <p className="col-span-2 md:col-span-3 text-white/40 text-[11px] -mt-1">
              This records a real settlement you're manually confirming you paid to the coverage provider. It's never
              created automatically and never counted twice against AutoHire's own net profit (that insurance money was
              already excluded from revenue).
            </p>
          </>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-4">
        <label data-testid="exp-form-receipt" className="inline-flex items-center gap-2 cursor-pointer border border-white/15 hover:border-[#F2B705]/50 rounded-md px-3 py-2 text-sm text-white/60 transition-colors">
          {uploading ? <Loader2 className="animate-spin" size={15} /> : <Upload size={15} />}
          {receipt ? "Receipt attached ✓" : "Upload receipt (image/PDF)"}
          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onFile} />
        </label>
        <button data-testid="exp-form-save" onClick={save} disabled={saving}
          className="ml-auto px-5 py-2 rounded-md bg-[#F2B705] text-black text-sm font-bold disabled:opacity-60">
          {saving ? "Saving…" : initial ? "Save Changes" : "Log Expense"}
        </button>
      </div>
    </Modal>
  );
}
